/**
 * ObjectIcon is a shared component that renders an icon based on a given object type name.
 *
 * @param {Object} iconProps - Same props as found in foundaitons-components Icon component.
 * @param {string} objectTypeName - The object type definition name to render an icon for.
 * @returns {React.ReactNode} The rendered icon.
 */

import { APPOINTMENT_TYPE_ID, CALL_TYPE_ID, CART_TYPE_ID, COMMUNICATION_TYPE_ID, COMPANY_TYPE_ID, CONTACT_TYPE_ID, COURSE_TYPE_ID, DEAL_TYPE_ID, EMAIL_TYPE_ID, INVOICE_TYPE_ID, LEAD_TYPE_ID, LISTING_TYPE_ID, MARKETING_EVENT_TYPE_ID, MEETING_EVENT_TYPE_ID, NOTE_TYPE_ID, ORDER_TYPE_ID, POSTAL_MAIL_TYPE_ID, PRODUCT_TYPE_ID, QUOTE_TYPE_ID, SERVICE_TYPE_ID, SUBSCRIPTION_TYPE_ID, TASK_TYPE_ID, TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { CallingIcon, CartIcon, SmsIcon, CompaniesIcon, ContactsIcon, DealsIcon, EmailIcon, InvoiceIcon, MarketingEventsIcon, MeetingsIcon, DescriptionIcon, OrderIcon, LogPostalMailIcon, ProductIcon, AddIcon, TicketIcon, RotateLeadsIcon, CustomObjectIcon, SalesQuoteIcon, AppointmentIcon, RealEstateListingIcon, CaseIcon, TasksIcon } from 'foundations-assets';
import { createElement, memo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const objectIconConfig = {
  [APPOINTMENT_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(AppointmentIcon, Object.assign({}, props))
  },
  [CALL_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(CallingIcon, Object.assign({}, props))
  },
  [CART_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(CartIcon, Object.assign({}, props))
  },
  [COMMUNICATION_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(SmsIcon, Object.assign({}, props))
  },
  [COMPANY_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(CompaniesIcon, Object.assign({}, props))
  },
  [CONTACT_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(ContactsIcon, Object.assign({}, props))
  },
  [COURSE_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(AddIcon, Object.assign({}, props))
  },
  [DEAL_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(DealsIcon, Object.assign({}, props))
  },
  [EMAIL_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(EmailIcon, Object.assign({}, props))
  },
  [INVOICE_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(InvoiceIcon, Object.assign({}, props))
  },
  [LEAD_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(RotateLeadsIcon, Object.assign({}, props))
  },
  [LISTING_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(RealEstateListingIcon, Object.assign({}, props))
  },
  [MARKETING_EVENT_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(MarketingEventsIcon, Object.assign({}, props))
  },
  [MEETING_EVENT_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(MeetingsIcon, Object.assign({}, props))
  },
  [NOTE_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(DescriptionIcon, Object.assign({}, props))
  },
  [ORDER_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(OrderIcon, Object.assign({}, props))
  },
  [POSTAL_MAIL_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(LogPostalMailIcon, Object.assign({}, props))
  },
  [PRODUCT_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(ProductIcon, Object.assign({}, props))
  },
  [QUOTE_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(SalesQuoteIcon, Object.assign({}, props))
  },
  [SERVICE_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(CaseIcon, Object.assign({}, props))
  },
  [SUBSCRIPTION_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(AddIcon, Object.assign({}, props))
  },
  [TASK_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(TasksIcon, Object.assign({}, props))
  },
  [TICKET_TYPE_ID]: {
    component: props => /*#__PURE__*/_jsx(TicketIcon, Object.assign({}, props))
  }
};

// Note: potential missing cases?
// LINE_ITEM,
// PARTNER_SERVICE,
// PARTNER_CLIENT,
// CONTRACT,
// COMMERCE_PAYMENT,
// PARTNER_ACCOUNT,
// CREDIT_MEMO,
// DEAL_REGISTRATION,
// PAYMENT_LINK,
// WHATSAPP_MESSAGE,
// LINKEDIN_MESSAGE
// other cases? https://tools.hubteam.com/lighthouse/objects?metaType=HUBSPOT&status=Live&visibility=Customer-facing -- do we need icons for all of these?

const ObjectIconComponent = ({
  objectTypeId,
  iconProps = {}
}) => {
  if (objectTypeId.startsWith('2')) {
    return /*#__PURE__*/_jsx(CustomObjectIcon, Object.assign({}, iconProps));
  }
  let config = null;
  if (Object.prototype.hasOwnProperty.call(objectIconConfig, objectTypeId)) {
    config = objectIconConfig[objectTypeId];
  }
  if (!config) {
    return null;
  }
  return /*#__PURE__*/createElement(config.component, iconProps);
};
export const ObjectIcon = /*#__PURE__*/memo(ObjectIconComponent);
ObjectIcon.displayName = 'ObjectIcon';